import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { format } from 'date-fns';

import MenuDotsIcon from '~/assets/icons/svgs/menu-dots.svg?react';
import CreateEditFolderModal from '~/components/folders/CreateEditFolderModal';
import DeleteFolderModal from '~/components/folders/DeleteFolderModal';
import DownloadFolderQRCodesModal from '~/components/folders/DownloadFolderQRCodesModal';
import { Text } from '~/components/ui/text';
import { PATH } from '~/constants/route-path';
import { COLORS } from '~/styles/colors';
import { QRCodeFolder } from '~/types/qrCodeFolders';
import { FormatLocales } from '~/utils/formatLocales';

type FolderItemProps = {
  folder: QRCodeFolder;
};

export default function FolderItem({ folder }: FolderItemProps) {
  const { i18n, t } = useTranslation();

  const styles = useStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpenEditFolderModal, setIsOpenEditFolderModal] = useState(false);
  const [isOpenDeleteFolderModal, setIsOpenDeleteFolderModal] = useState(false);
  const [isOpenDownloadFolderQRsModal, setIsOpenDownloadFolderQRsModal] = useState(false);

  return (
    <>
      <StyledFolderLink to={`${PATH.QR.MAIN}/folders/${folder.id}/${folder.name}`}>
        <Box sx={styles.top}>
          <Text variant="t16Medium" sx={{ wordBreak: 'break-word' }}>
            {folder.name}
          </Text>
          <IconButton
            disableRipple
            sx={styles.menuDotsWrapper}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
            }}
          >
            <MenuDotsIcon />
          </IconButton>
        </Box>
        <Box sx={styles.bottom}>
          <Text variant="inputVerySmall" sx={{ color: COLORS.grey400 }}>
            {format(folder.createdAtRaw, 'MMMM d, yyyy', { locale: FormatLocales[i18n.language] })}
          </Text>
          <Text variant="input15" sx={{ padding: '2px 8px', borderRadius: '50%', backgroundColor: COLORS.grey500 }}>
            {folder.qrCodesCount}
          </Text>
        </Box>
      </StyledFolderLink>

      {isOpenEditFolderModal && (
        <CreateEditFolderModal
          isOpen={isOpenEditFolderModal}
          onClose={() => setIsOpenEditFolderModal(false)}
          folderId={folder.id}
          defaultFolderName={folder.name}
          type="edit"
        />
      )}
      <Menu
        MenuListProps={{
          sx: styles.menuList,
        }}
        slotProps={{
          paper: {
            sx: styles.menuPaper,
          },
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setIsOpenEditFolderModal(true);
            setAnchorEl(null);
          }}
          sx={styles.menuItem}
        >
          <Text variant="input15" sx={{ color: COLORS.textMain }}>
            {t('folders:editFolderName')}
          </Text>
        </MenuItem>
        {folder.qrCodesCount > 0 && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setIsOpenDownloadFolderQRsModal(true);
            }}
            sx={styles.menuItem}
          >
            <Text variant="input15" sx={{ color: COLORS.textMain }}>
              {t('folders:downloadQRs')}
            </Text>
          </MenuItem>
        )}
        <MenuItem
          sx={styles.menuItem}
          onClick={() => {
            setIsOpenDeleteFolderModal(true);
            setAnchorEl(null);
          }}
        >
          <Text variant="input15" sx={{ color: COLORS.error }}>
            {t('folders:deleteFolder')}
          </Text>
        </MenuItem>
      </Menu>
      {isOpenDownloadFolderQRsModal && (
        <DownloadFolderQRCodesModal
          isOpen={isOpenDownloadFolderQRsModal}
          onClose={() => setIsOpenDownloadFolderQRsModal(false)}
          folderId={folder.id}
          folderName={folder.name}
        />
      )}
      {isOpenDeleteFolderModal && (
        <DeleteFolderModal
          isOpen={isOpenDeleteFolderModal}
          onClose={() => setIsOpenDeleteFolderModal(false)}
          folderId={folder.id}
        />
      )}
    </>
  );
}

function useStyles() {
  return {
    top: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
    `,
    bottom: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-top: 12px;
    `,
    menuDotsWrapper: css`
      border: 1px solid ${COLORS.grey200};
      padding: 4px;
      width: 28px;
      height: 28px;
      box-sizing: border-box;
      flex-shrink: 0;
    `,
    menuPaper: css`
      border-radius: 20px;
      box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.05);
      padding: 20px;
      border: 1px solid ${COLORS.grey200};
      transform: translateY(3px) !important;
    `,
    menuList: css`
      padding: 0;
    `,
    menuItem: css`
      padding: 0;

      &:not(:first-of-type) {
        margin-top: 16px;
      }

      &:hover {
        background-color: ${COLORS.white};
      }
    `,
  };
}

const StyledFolderLink = styled(Link)`
  background-color: ${COLORS.white};
  border-radius: 16px;
  padding: 16px 20px;
  border: 2px solid ${COLORS.white};
  min-width: 250px;
  max-width: 250px;
  box-sizing: border-box;
  width: fit-content;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    border-color: ${COLORS.primary};
    text-decoration: none;
  }
`;
